import {
  fetchBjList,
  fetchBjNotice,
  fetchFanHideAdd,
  fetchFanHideDelete,
  fetchLive,
  fetchLiveBookmark,
  fetchLiveFan,
  fetchLiveGift,
  fetchLivePlay,
  fetchLiveRanking,
  fetchLiveRoomRank,
  fetchLiveUpcoming,
  fetchLiveUrl,
  fetchLiveWatch,
  fetchMemberInfo,
  fetchPlayUserCount,
  fetchRandomMediaInfo,
  fetchWatchDelete,
  postLiveExcelSpon,
  postLiveRecommend,
  postLiveSpon,
} from '@module/apis/client-side/live';
import type {
  BjListParams,
  BjListResponse,
  FetchBjNoticeParams,
  FetchFanHideAddParams,
  FetchFanHideDeleteParams,
  FetchLiveBookmarkParams,
  FetchLiveBookmarkResponse,
  FetchLiveFanParams,
  FetchLiveFanResponse,
  FetchLiveGiftParams,
  FetchLiveGiftResponse,
  FetchLiveRoomRankParams,
  FetchLiveRoomRankResponse,
  FetchLiveUpcomingParams,
  FetchLiveUpcomingResponse,
  FetchLiveWatchParams,
  FetchLiveWatchResponse,
  FetchMemberInfoParams,
  FetchWatchDeleteParams,
  LiveItems,
  LiveParams,
  LivePlayParams,
  LiveRankingParams,
  LiveRankingResponse,
  LiveUrl,
  LiveUrlParams,
  MemberInfoResponse,
  PlayInfo,
  PlayUserCount,
  PlayUserCountParams,
  PostLiveExcelSponParams,
  PostLiveExcelSponResponse,
  PostLiveRecommendParams,
  PostLiveRecommendResponse,
  PostLiveSponParams,
  PostLiveSponResponse,
} from '@module/apis/client-side/live/type';
import type { ApiBaseResponse } from '@module/apis/type';
import { attachAdultShowAdModeParamIfEligible } from '@module/helpers/adult/adultShowAdMode';
import { useAdultShowAdModeEligibility } from '@module/helpers/adult/useAdultShowAdMode';
import usePartnerStore from '@module/stores/usePartnerStore';
import type { EssentialMediaInfo } from '@module/types/play/goPlayTypes';
import {
  keepPreviousData,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useRef } from 'react';
import { filterDuplicatesByUserIdx } from '../../utils/filter/filterDuplicatesByUserIdx';
import TimeConstants from '../../utils/item';
import type { CustomUseQueryOptions } from '../type';
import { liveQueryKeys } from './liveQueryKeys';

type PageParam = { limit: number; offset: number };

export const useFetchLiveInfiniteQuery = (
  fetchLiveParams: LiveParams,
  options?: Partial<
    Omit<
      UseInfiniteQueryOptions<LiveItems, Error, LiveItems, LiveItems, QueryKey, PageParam>,
      'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
    >
  >,
) => {
  const { AdultShowAdMode } = usePartnerStore();
  const { isResolved, adultToggleShow } = useAdultShowAdModeEligibility();
  const finalParams = attachAdultShowAdModeParamIfEligible(fetchLiveParams, {
    adultShowAdMode: AdultShowAdMode,
    adultToggleShow,
    isResolved,
  });
  const initialPageParam = { limit: finalParams.limit, offset: 0 };
  const flipped = useRef(false);

  const query = useInfiniteQuery({
    initialPageParam,
    queryKey: liveQueryKeys.fetchLiveInfinite(finalParams).queryKey,
    queryFn: async ({ pageParam }) => {
      return fetchLive({
        ...finalParams,
        limit: pageParam.limit,
        offset: pageParam.offset,
      });
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    ...options,
    enabled: (options?.enabled ?? true) && isResolved,
    getNextPageParam: (lastPage) => {
      if (lastPage.page.limit + lastPage.page.offset > lastPage.page.total) return;

      if (lastPage.page.offset > 0) flipped.current = true;

      if (lastPage.list.length + initialPageParam.limit < 100 && flipped.current === false) {
        return {
          limit: initialPageParam.limit + lastPage.page.limit,
          offset: 0,
        };
      }
      flipped.current = true;
      return {
        limit: initialPageParam.limit + 5,
        offset: lastPage.page.limit + lastPage.page.offset - 5,
      };
    },
    select: (data) => {
      return {
        pages: filterDuplicatesByUserIdx(data?.pages.flatMap((page) => page.list)),
        pageParams: data.pageParams,
        currentPage: data?.pages[data?.pages.length - 1].page.page,
        lastpage: data?.pages[data?.pages.length - 1].page.lastPage,
        total: data?.pages.length > 0 ? data?.pages[0].page.total : 0,
      };
    },
  });

  return {
    ...query,
    finalParams,
  };
};

export const useFetchBjListInfiniteQuery = (
  fetchBjListParams: BjListParams,
  options?: Partial<UseInfiniteQueryOptions<BjListResponse, Error>>,
) => {
  const { AdultShowAdMode } = usePartnerStore();
  const { isResolved, adultToggleShow } = useAdultShowAdModeEligibility();
  const finalParams = attachAdultShowAdModeParamIfEligible(fetchBjListParams, {
    adultShowAdMode: AdultShowAdMode,
    adultToggleShow,
    isResolved,
  });

  const query = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: liveQueryKeys.fetchBjList(finalParams).queryKey,
    queryFn: ({ pageParam = 1 }: { pageParam?: unknown }) => {
      const actualPageParam = typeof pageParam === 'number' ? pageParam : 1;
      const offset = finalParams.limit * ((actualPageParam || 1) - 1);
      return fetchBjList({ ...finalParams, offset });
    },
    ...options,
    enabled: !!finalParams?.searchVal?.length && (options?.enabled ?? true) && isResolved,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetchedItems = allPages.flatMap((page) => page.list).length;

      if (totalFetchedItems < lastPage.page.total) {
        const nextPage = allPages.length + 1;
        return nextPage;
      }
      return undefined;
    },
    select: (data) => {
      return {
        pages: data?.pages.flatMap((page) => page.list),
        pageParams: data.pageParams,
        total: data?.pages.length > 0 ? data?.pages[0].page.total : 0,
      };
    },
  });

  return {
    ...query,
    finalParams,
  };
};

export const useFetchLiveRankingInfiniteQuery = (
  fetchLiveRankingParams: LiveRankingParams,
  options?: Partial<UseInfiniteQueryOptions<LiveRankingResponse, Error>>,
) => {
  const { AdultShowAdMode } = usePartnerStore();
  const { isResolved, adultToggleShow } = useAdultShowAdModeEligibility();
  const finalParams = attachAdultShowAdModeParamIfEligible(fetchLiveRankingParams, {
    adultShowAdMode: AdultShowAdMode,
    adultToggleShow,
    isResolved,
  });

  const query = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: liveQueryKeys.fetchLiveRanking(finalParams).queryKey,
    queryFn: ({ pageParam = 1 }: { pageParam?: unknown }) => {
      const actualPageParam = typeof pageParam === 'number' ? pageParam : 1;
      const offset = finalParams.limit * ((actualPageParam || 1) - 1);
      return fetchLiveRanking({ ...finalParams, offset });
    },
    ...options,
    enabled: (options?.enabled ?? true) && isResolved,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetchedItems = allPages.flatMap((page) => page.list).length;

      if (totalFetchedItems < lastPage.page.total && totalFetchedItems <= 200) {
        const nextPage = allPages.length + 1;
        return nextPage;
      }
      return undefined;
    },
    select: (data) => ({
      pages: data?.pages.flatMap((page) => page.list),
      pageParams: data.pageParams,
    }),
  });

  return {
    ...query,
    finalParams,
  };
};

export const useFetchLiveQuery = (fetchLiveParams: LiveParams, options?: CustomUseQueryOptions<LiveItems, Error>) => {
  const { AdultShowAdMode } = usePartnerStore();
  const { isResolved, adultToggleShow } = useAdultShowAdModeEligibility();
  const finalParams = attachAdultShowAdModeParamIfEligible(fetchLiveParams, {
    adultShowAdMode: AdultShowAdMode,
    adultToggleShow,
    isResolved,
  });

  const query = useQuery({
    queryKey: liveQueryKeys.fetchLive(finalParams).queryKey,
    queryFn: () => fetchLive(finalParams),
    ...options,
    staleTime: TimeConstants.HALF_SECOND * 10,
    enabled: (options?.enabled ?? true) && isResolved,
    placeholderData: keepPreviousData,
  });

  return {
    ...query,
    finalParams,
  };
};

export const useFetchPlayUserCountQuery = (
  params: PlayUserCountParams,
  options?: CustomUseQueryOptions<PlayUserCount, Error>,
) =>
  useQuery({
    queryKey: liveQueryKeys.fetchLivePlaycount(params).queryKey,
    queryFn: () => fetchPlayUserCount(params),
    ...options,
    staleTime: TimeConstants.HALF_SECOND * 10,
    placeholderData: keepPreviousData,
  });

export const useFetchLivePlayMutation = (options?: UseMutationOptions<PlayInfo, Error, LivePlayParams>) =>
  useMutation({
    mutationKey: liveQueryKeys.livePlay().queryKey,
    mutationFn: (fetchLivePlayParams: LivePlayParams) => fetchLivePlay(fetchLivePlayParams),
    ...options,
  });

export const useFetchLiveUrlMutation = (options?: UseMutationOptions<LiveUrl, Error, LiveUrlParams>) =>
  useMutation({
    mutationKey: liveQueryKeys.liveUrl().queryKey,
    mutationFn: (params: LiveUrlParams) => fetchLiveUrl(params),
    ...options,
  });

export const useFetchLiveRankingQuery = (
  fetchLiveRankingParams: LiveRankingParams,
  options?: CustomUseQueryOptions<LiveRankingResponse, Error>,
) => {
  const { AdultShowAdMode } = usePartnerStore();
  const { isResolved, adultToggleShow } = useAdultShowAdModeEligibility();
  const finalParams = attachAdultShowAdModeParamIfEligible(fetchLiveRankingParams, {
    adultShowAdMode: AdultShowAdMode,
    adultToggleShow,
    isResolved,
  });

  const query = useQuery({
    queryKey: liveQueryKeys.fetchLiveRanking(finalParams).queryKey,
    queryFn: () => fetchLiveRanking(finalParams),
    ...options,
    enabled: (options?.enabled ?? true) && isResolved,
  });

  return {
    ...query,
    finalParams,
  };
};

export const useFetchLiveRoomRankMutation = (
  fetchLiveRoomRankParams: FetchLiveRoomRankParams,
  options?: UseMutationOptions<PostLiveRecommendResponse, Error>,
) =>
  useMutation({
    mutationKey: liveQueryKeys.fetchLiveRoomRank(fetchLiveRoomRankParams).queryKey,
    mutationFn: () => fetchLiveRoomRank(fetchLiveRoomRankParams),
    ...options,
  });

export const useFetchMemberInfoQuery = (
  params?: FetchMemberInfoParams,
  options?: CustomUseQueryOptions<MemberInfoResponse, Error>,
) =>
  useQuery({
    queryKey: liveQueryKeys.fetchMemberinfo(params).queryKey,
    queryFn: () => fetchMemberInfo(params),
    staleTime: Infinity,
    ...options,
  });

export const usePostLiveSponMutation = (
  options?: UseMutationOptions<PostLiveSponResponse, Error, PostLiveSponParams>,
  isASP: boolean = false,
) =>
  useMutation({
    mutationKey: liveQueryKeys.postLiveSpon().queryKey,
    mutationFn: (postLiveSponParams: PostLiveSponParams) => postLiveSpon(postLiveSponParams, isASP),
    ...options,
  });

export const usePostLiveExcelSponMutation = (
  options?: UseMutationOptions<PostLiveExcelSponResponse, Error, PostLiveExcelSponParams>,
) =>
  useMutation({
    mutationKey: liveQueryKeys.postLiveExcelSpon().queryKey,
    mutationFn: (postLiveExcelSponParams: PostLiveExcelSponParams) => postLiveExcelSpon(postLiveExcelSponParams),
    ...options,
  });

export const useFetchLiveFanInfiniteQuery = (
  fetchLiveFanParams: FetchLiveFanParams,
  options?: Partial<UseInfiniteQueryOptions<FetchLiveFanResponse, Error>>,
) =>
  useInfiniteQuery({
    initialPageParam: 1,
    queryKey: liveQueryKeys.fetchLiveFanInfinite(fetchLiveFanParams).queryKey,
    queryFn: ({ pageParam = 1 }: { pageParam?: unknown }) => {
      const actualPageParam = typeof pageParam === 'number' ? pageParam : 1;
      const offset = fetchLiveFanParams.limit * ((actualPageParam || 1) - 1);
      return fetchLiveFan({ ...fetchLiveFanParams, offset });
    },
    ...options,
    staleTime: TimeConstants.HALF_SECOND * 10,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetchedItems = allPages.flatMap((page) => page.list).length;

      if (totalFetchedItems < lastPage.page.total) {
        const nextPage = allPages.length + 1;
        return nextPage;
      }
      return undefined;
    },
    select: (data) => {
      return {
        hideCount: data.pages.reduce((acc, cur) => acc + (cur.hideCount ?? 0), 0),
        pages: data?.pages.flatMap((page) => page.list),
        pageParams: data.pageParams,
        total: data?.pages.length > 0 ? data?.pages[0].page.total : 0,
      };
    },
  });

export const useFetchLiveGiftInfiniteQuery = (
  fetchLiveGiftParams: FetchLiveGiftParams,
  options?: Partial<UseInfiniteQueryOptions<FetchLiveGiftResponse, Error>>,
) =>
  useInfiniteQuery({
    initialPageParam: 1,
    queryKey: liveQueryKeys.fetchLiveGiftInfinite(fetchLiveGiftParams).queryKey,
    queryFn: ({ pageParam = 1 }: { pageParam?: unknown }) => {
      const actualPageParam = typeof pageParam === 'number' ? pageParam : 1;
      const offset = fetchLiveGiftParams.limit * ((actualPageParam || 1) - 1);
      return fetchLiveGift({ ...fetchLiveGiftParams, offset });
    },
    ...options,
    staleTime: TimeConstants.HALF_SECOND * 10,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetchedItems = allPages.flatMap((page) => page.list).length;

      if (totalFetchedItems < lastPage.page.total) {
        const nextPage = allPages.length + 1;
        return nextPage;
      }
      return undefined;
    },
    select: (data) => {
      return {
        pages: data?.pages.flatMap((page) => page.list),
        pageParams: data.pageParams,
        total: data?.pages.length > 0 ? data?.pages[0].page.total : 0,
      };
    },
  });

export const useFetchLiveWatchInfiniteQuery = (
  fetchLiveWatchParams: FetchLiveWatchParams,
  options?: Partial<UseInfiniteQueryOptions<FetchLiveWatchResponse, Error>>,
) =>
  useInfiniteQuery({
    initialPageParam: 1,
    queryKey: liveQueryKeys.fetchLiveWatchInfinite(fetchLiveWatchParams).queryKey,
    queryFn: ({ pageParam = 1 }: { pageParam?: unknown }) => {
      const actualPageParam = typeof pageParam === 'number' ? pageParam : 1;
      const offset = fetchLiveWatchParams.limit * ((actualPageParam || 1) - 1);
      return fetchLiveWatch({ ...fetchLiveWatchParams, offset });
    },
    ...options,
    staleTime: TimeConstants.HALF_SECOND * 10,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetchedItems = allPages.flatMap((page) => page.list).length;

      if (totalFetchedItems < lastPage.page.total) {
        const nextPage = allPages.length + 1;
        return nextPage;
      }
      return undefined;
    },
    select: (data) => {
      return {
        pages: data?.pages.flatMap((page) => page.list),
        pageParams: data.pageParams,
        total: data?.pages.length > 0 ? data?.pages[0].page.total : 0,
      };
    },
  });

export const useFetchLiveBookmarkInfiniteQuery = (
  fetchLiveBookmarkParams: FetchLiveBookmarkParams,
  options?: Partial<UseInfiniteQueryOptions<FetchLiveBookmarkResponse, Error>>,
) =>
  useInfiniteQuery({
    initialPageParam: 1,
    queryKey: liveQueryKeys.fetchLiveBookmarkInfinite(fetchLiveBookmarkParams).queryKey,
    queryFn: ({ pageParam = 1 }: { pageParam?: unknown }) => {
      const actualPageParam = typeof pageParam === 'number' ? pageParam : 1;
      const offset = fetchLiveBookmarkParams.limit * ((actualPageParam || 1) - 1);
      return fetchLiveBookmark({ ...fetchLiveBookmarkParams, offset });
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetchedItems = allPages.flatMap((page) => page.list).length;

      if (totalFetchedItems < lastPage.page.total) {
        const nextPage = allPages.length + 1;
        return nextPage;
      }
      return undefined;
    },
    ...options,
    staleTime: TimeConstants.HALF_SECOND * 10,
    placeholderData: keepPreviousData,
    select: (data) => {
      return {
        pages: data?.pages.flatMap((page) => page.list),
        pageParams: data.pageParams,
        total: data?.pages.length > 0 ? data?.pages[0].page.total : 0,
      };
    },
  });

export const useFanHideAddMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, FetchFanHideAddParams>) =>
  useMutation({
    mutationKey: liveQueryKeys.fetchFanHideAdd().queryKey,
    mutationFn: (params: FetchFanHideAddParams) => fetchFanHideAdd(params),
    ...options,
  });

export const useFanHideDeleteMutation = (
  options?: UseMutationOptions<ApiBaseResponse, Error, FetchFanHideDeleteParams>,
) =>
  useMutation({
    mutationKey: liveQueryKeys.fetchFanHideDelete().queryKey,
    mutationFn: (params: FetchFanHideDeleteParams) => fetchFanHideDelete(params),
    ...options,
  });

export const useWatchDeleteMutation = (options?: UseMutationOptions<ApiBaseResponse, Error, FetchWatchDeleteParams>) =>
  useMutation({
    mutationKey: liveQueryKeys.fetchWatchDelete().queryKey,
    mutationFn: (params: FetchWatchDeleteParams) => fetchWatchDelete(params),
    ...options,
  });

export const useFetchLiveRoomRankListMutation = (
  options?: UseMutationOptions<FetchLiveRoomRankResponse, Error, FetchLiveRoomRankParams>,
) =>
  useMutation({
    mutationKey: liveQueryKeys.fetchLiveRoomRankList().queryKey,
    mutationFn: (fetchLiveRoomRankParams) => fetchLiveRoomRank(fetchLiveRoomRankParams),
    ...options,
  });

export const usePostRecommendMutation = (
  options?: UseMutationOptions<PostLiveRecommendResponse, Error, PostLiveRecommendParams>,
) =>
  useMutation({
    mutationKey: liveQueryKeys.postRecommend().queryKey,
    mutationFn: (postLiveRecommendParams) => postLiveRecommend(postLiveRecommendParams),
    ...options,
  });

export const useFetchFetchBjNotice = (
  fetchBjNoticeParams: FetchBjNoticeParams,
  options?: CustomUseQueryOptions<ApiBaseResponse, Error>,
) =>
  useQuery({
    queryKey: liveQueryKeys.fetchBjNotice(fetchBjNoticeParams).queryKey,
    queryFn: () => fetchBjNotice(fetchBjNoticeParams),
    ...options,
  });

export const useFetchLiveUpcomingQuery = (
  fetchLiveUpcomingParams: FetchLiveUpcomingParams,
  options?: CustomUseQueryOptions<FetchLiveUpcomingResponse, Error>,
) =>
  useQuery({
    queryKey: liveQueryKeys.fetchLiveUpcoming(fetchLiveUpcomingParams).queryKey,
    queryFn: () => fetchLiveUpcoming(fetchLiveUpcomingParams),
    ...options,
  });

// 매번 "다른" 랜덤 미디어를 받기 위한 훅이라 useQuery 캐시를 쓰지 않는다.
// useQuery + staleTime/gcTime 0 조합으로 회피했더니 첫 렌더에 직전 cached userId 로
// 그대로 redirect 해 같은(혹은 이미 오프라인인) BJ 로 다시 들어가는 버그가 있었음.
export const useFetchRandomMediaMutation = (options?: UseMutationOptions<EssentialMediaInfo | null, Error, void>) =>
  useMutation({
    mutationKey: liveQueryKeys.fetchRandomMedia().queryKey,
    mutationFn: () => fetchRandomMediaInfo(),
    ...options,
  });
